import * as React from "react"

import { navigation } from "@/data/menu"
import { useModulePermission } from "@/features/auth/api/get-module-permission"
import { SidebarNavItem } from "@/types/nav"

const routeModules = navigation.flatMap((item) => {
  const scoped = item.items.filter((sub) => sub.modules?.length)
  const source = scoped.length > 0 ? scoped : [item]

  return source.flatMap((nav) =>
    nav.path.map((path) => ({ path, modules: nav.modules ?? [] }))
  )
})

export const modulesForPath = (pathname: string): string[] => {
  const match = routeModules
    .filter(
      (route) => pathname === route.path || pathname.startsWith(route.path + "/")
    )
    .sort((a, b) => b.path.length - a.path.length)[0]

  return match?.modules ?? []
}

export const useAllowedModules = () => {
  const { data, isLoading } = useModulePermission({})

  const allowed = React.useMemo(
    () => new Set((data ?? []).map((module) => module.code)),
    [data]
  )

  return { allowed, isLoading }
}

export const canAccess = (nav: SidebarNavItem, allowed: Set<string>) =>
  !nav.modules?.length || nav.modules.some((module) => allowed.has(module))

export const useAllowedNavigation = () => {
  const { allowed, isLoading } = useAllowedModules()

  const items = React.useMemo(() => {
    if (isLoading) return []

    return navigation
      .map((item) => {
        const items = item.items.filter((sub) => canAccess(sub, allowed))
        return { ...item, href: items[0]?.href ?? item.href, items }
      })
      .filter((item) => canAccess(item, allowed) && item.items.length > 0)
  }, [allowed, isLoading])

  return { items, isLoading }
}
